import React, { useEffect, useRef, useCallback, forwardRef } from 'react';
import clsx from 'clsx';

import css from './TextArea.module.scss';
import fontsCss from 'components/ui/Typography/Typography.module.scss';

interface Props {
    error?: boolean;
    className?: string;
}

const TextArea = forwardRef<HTMLTextAreaElement, Props & React.HTMLProps<HTMLTextAreaElement>>(
    ({ error, className, ...props }, ref) => {
        const textAreaRef = useRef<HTMLTextAreaElement>(null);
        const combinedRef = (ref as React.RefObject<HTMLTextAreaElement>) || textAreaRef;

        // Prevent listeners in parent components from triggering when typing is in progress
        const handleKeyDown = useCallback((event: KeyboardEvent) => {
            event.stopPropagation();
        }, []);

        useEffect(() => {
            const textAreaElement = combinedRef.current;
            if (textAreaElement) {
                textAreaElement.addEventListener('keydown', handleKeyDown);
            }

            return () => {
                if (textAreaElement) {
                    textAreaElement.removeEventListener('keydown', handleKeyDown);
                }
            };
        }, [handleKeyDown, combinedRef]);

        return (
            <textarea
                ref={combinedRef}
                className={clsx(
                    css.textarea,
                    'bg-gray-50 border border-gray-200',
                    fontsCss.typography,
                    fontsCss.figma_BodyMedNormal,
                    error && css.error,
                    className,
                )}
                {...props}
            />
        );
    },
);

TextArea.displayName = 'TextArea';

export default React.memo(TextArea);
